import { type JSX } from "react";
import { DeleteRegular, AddRegular, LockClosedRegular } from "@fluentui/react-icons";
import { DealDateInput } from "@/components/Workflow/QuickActions/CreateDeal/DealDateInput";
import { formatNumericCalendarDate, toIsoCalendarDate } from "@/utils/calendar-date";
import type {
  TriyoDynamicFieldTable,
  TriyoDynamicFieldTableCell,
  TriyoDynamicFieldTableColumn,
  TriyoDynamicFieldTableRow,
} from "@/types/models";
import { EMPTY_CELL, cellOptions, columnKind, findCell, isCellLocked, visibleColumns } from "./deal-tracker-rows";

/** Columns held still while the rest of the grid scrolls (Freeze Pane, TAD-6037). */
const FROZEN_COLUMN_COUNT = 2;

export interface DealTrackerTableProps {
  table: TriyoDynamicFieldTable;
  /** Read-only rendering — the caller may look at the tracker but not change it. */
  disabled?: boolean;
  onCellChange: (rowKey: number, columnId: number, value: string) => void;
  onAddRow: () => void;
  onDeleteRow: (rowKey: number) => void;
}

function frozenClass(index: number): string {
  if (index >= FROZEN_COLUMN_COUNT) return "";
  return ` deal-tracker__cell--frozen deal-tracker__cell--frozen-${index + 1}`;
}

interface CellEditorProps {
  row: TriyoDynamicFieldTableRow;
  column: TriyoDynamicFieldTableColumn;
  cell: TriyoDynamicFieldTableCell;
  disabled: boolean;
  onChange: (value: string) => void;
}

function CellEditor({ row, column, cell, disabled, onChange }: CellEditorProps): JSX.Element {
  const kind = columnKind(column);
  const label = `${column.Name ?? "Value"}, row ${row.Row}`;
  const value = cell.Value ?? "";

  // Columns with no add-in editor (and cells the admin locked outright) still
  // show their value so the row reads correctly -- they just cannot be typed in.
  if (kind === "unsupported" || disabled || isCellLocked(cell)) {
    return (
      <span className="deal-tracker__readonly" title={value || undefined}>
        {value || EMPTY_CELL}
        {isCellLocked(cell) ? <LockClosedRegular aria-label="Entry locked" /> : null}
      </span>
    );
  }

  if (kind === "list") {
    const options = cellOptions(cell, column);
    // A saved value the admin has since removed from the list stays selectable,
    // so opening the tracker cannot silently drop it.
    const all = value && !options.includes(value) ? [value, ...options] : options;
    return (
      <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{EMPTY_CELL}</option>
        {all.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (kind === "date") {
    return (
      <DealDateInput
        aria-label={label}
        value={toIsoCalendarDate(value)}
        onChange={(event) => onChange(event.target.value ? formatNumericCalendarDate(event.target.value) : "")}
      />
    );
  }

  return (
    <input
      type={kind === "number" ? "number" : "text"}
      inputMode={kind === "number" ? "decimal" : undefined}
      step={kind === "number" ? "any" : undefined}
      aria-label={label}
      placeholder={EMPTY_CELL}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/**
 * The Deal Tracker grid: every column the admin configured, with the first two
 * frozen and the rest scrolling horizontally. Rows are keyed by `Row` rather
 * than `Id` because every unsaved row shares Id 0.
 */
export function DealTrackerTable({
  table,
  disabled = false,
  onCellChange,
  onAddRow,
  onDeleteRow,
}: DealTrackerTableProps): JSX.Element {
  const columns = visibleColumns(table);

  return (
    <div className="deal-tracker__table-area">
      <div className="deal-tracker__table-wrap">
        <table className="deal-tracker__table" aria-label="Deal Tracker">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={column.Id} scope="col" className={`deal-tracker__head${frozenClass(index)}`}>
                  {column.Name}
                </th>
              ))}
              {disabled ? null : (
                <th scope="col" className="deal-tracker__actions-head">
                  <span className="deal-tracker__visually-hidden">Row actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {table.Rows.map((row) => (
              <tr key={row.Row}>
                {columns.map((column, index) => {
                  const cell = findCell(row, column.Id);
                  return (
                    <td key={column.Id} className={`deal-tracker__cell${frozenClass(index)}`}>
                      {cell ? (
                        <CellEditor
                          row={row}
                          column={column}
                          cell={cell}
                          disabled={disabled}
                          onChange={(value) => onCellChange(row.Row, column.Id, value)}
                        />
                      ) : (
                        <span className="deal-tracker__readonly">{EMPTY_CELL}</span>
                      )}
                    </td>
                  );
                })}
                {disabled ? null : (
                  <td className="deal-tracker__actions-cell">
                    <button
                      type="button"
                      className="deal-tracker__row-delete"
                      aria-label={`Delete row ${row.Row}`}
                      onClick={() => onDeleteRow(row.Row)}
                    >
                      <DeleteRegular />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {table.Rows.length === 0 ? (
        <p className="deal-tracker__empty-note">
          {disabled ? "No deals are logged on this tracker." : "No deals yet — add your first row."}
        </p>
      ) : null}

      {disabled ? null : (
        <button type="button" className="deal-tracker__add-row" onClick={onAddRow}>
          <AddRegular /> Add a row
        </button>
      )}
    </div>
  );
}
